/**
 * Details for the selected mission or antenna: the record, its analysis
 * outcome and the upcoming passes. Actions: follow, fly to, ground track.
 * Spacing and the font follow the density setting; a stale TLE and a pass
 * in progress say so in words as well as colour.
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Crosshair, Footprints, Navigation, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router';
import { clockNow, useUI } from '@/state/store';
import { registerAntennaTwin, registerMissionTwin } from '@/features/twin/data';
import { toast } from '@/components/ui/toast';
import { Chip, TextButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { VISUAL } from '../../constants';
import { getGlobeEngine } from '../../globe/engineHandle';
import { useLoader } from '../../store';
import { StationWeather } from './StationWeather';
import type { ContactWindow } from '../../types';
import {
  bandShort,
  fmtMinutes,
  fmtUtcShort,
  missionBands,
  orbitLabel,
  pct,
  slaTone,
  tierFor,
  utilTone,
} from '../format';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-cell-y">
      <span className="shrink-0 text-ink-3">{label}</span>
      <span className="text-right text-ink">{children}</span>
    </div>
  );
}

function Bands({ bands }: { bands: string[] }) {
  return (
    <span className="inline-flex flex-wrap justify-end gap-1">
      {bands.map((b) => (
        <span
          key={b}
          className="rounded-control border border-hairline px-1.5 py-0.5 font-mono text-[10px] text-ink-2"
        >
          {bandShort(b)}
        </span>
      ))}
    </span>
  );
}

function tleAgeTone(days: number | undefined): 'nominal' | 'caution' | 'warning' | 'neutral' {
  if (days === undefined) return 'neutral';
  if (days > VISUAL.TLE_STALE_DAYS) return 'warning';
  if (days > VISUAL.TLE_WARN_DAYS) return 'caution';
  return 'nominal';
}

function tleAgeDays(line1: string | undefined, nowMs: number): number | undefined {
  if (!line1 || line1.length < 32) return undefined;
  const yy = Number(line1.slice(18, 20));
  const doy = Number(line1.slice(20, 32));
  if (!Number.isFinite(yy) || !Number.isFinite(doy)) return undefined;
  const year = yy < 57 ? 2000 + yy : 1900 + yy;
  const epoch = Date.UTC(year, 0, 1) + (doy - 1) * 86_400_000;
  return (nowMs - epoch) / 86_400_000;
}

interface Pass extends ContactWindow {
  counterpart: string;
}

function upcoming(windows: Record<string, ContactWindow[]> | undefined, nowMs: number, limit = 6): Pass[] {
  if (!windows) return [];
  const all: Pass[] = [];
  for (const [counterpart, list] of Object.entries(windows))
    for (const w of list) if (w.endMs >= nowMs) all.push({ ...w, counterpart });
  return all.sort((a, b) => a.startMs - b.startMs).slice(0, limit);
}

export function SelectionPanel() {
  const selection = useUI((s) => s.selection);
  const dataset = useLoader((s) => s.dataset);
  const results = useLoader((s) => s.results);
  const tracked = useLoader((s) => s.trackedMissionId);
  const setTracked = useLoader((s) => s.setTracked);
  const navigate = useNavigate();
  const nowMs = clockNow();
  // TLE age is against wall time (not the simulated clock), re-read once a minute.
  const [wallNow, setWallNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setWallNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const mission = useMemo(
    () => dataset?.missions.find((m) => m.id === selection.missionId),
    [dataset, selection.missionId],
  );
  const antenna = useMemo(
    () => dataset?.antennas.find((a) => a.id === selection.antennaId),
    [dataset, selection.antennaId],
  );

  if (!dataset) return <div className="p-4 text-xs text-ink-3">Loading the network.</div>;

  if (mission) {
    const tier = tierFor(dataset.tiers, mission.priorityTier);
    const sla = results?.missionSLA[mission.id];
    const age = tleAgeDays(mission.orbit.tle?.line1, wallNow);
    const passes = upcoming(results?.contactWindows[mission.id], nowMs);
    const antennaName = (id: string) =>
      dataset.antennas.find((a) => a.id === id)?.shortName ??
      dataset.tdrss.find((t) => t.id === id)?.shortName ??
      id;
    return (
      <div className="flex flex-col gap-3 p-panel text-data">
        <div>
          <div className="font-display text-base font-semibold text-ink">{mission.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Chip tone="neutral">
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: tier?.color ?? '#8a93a6' }}
              />
              Tier {mission.priorityTier}
              {tier ? `, ${tier.name}` : ''}
            </Chip>
            {mission.agency && <Chip tone="neutral">{mission.agency}</Chip>}
            {mission.status && <Chip tone="caution">{mission.status}</Chip>}
            {age !== undefined && (
              <Chip tone={tleAgeTone(age)} className="font-mono">
                {(tleAgeTone(age) === 'warning' || tleAgeTone(age) === 'caution') && (
                  <TriangleAlert aria-hidden className="h-3 w-3" />
                )}
                TLE {age.toFixed(1)} d
                {tleAgeTone(age) === 'warning' && <span className="sr-only">, stale</span>}
                {tleAgeTone(age) === 'caution' && <span className="sr-only">, ageing</span>}
              </Chip>
            )}
          </div>
          {mission.description && <p className="mt-2 leading-snug text-ink-2">{mission.description}</p>}
          {mission.orbit.tleStatus === 'unverified' && <p className="mt-2 text-warning">{mission.orbit.tleIssue}. No predicted orbit or contacts are shown for this unverified record.</p>}
          {!mission.orbit.tle && mission.orbit.type === 'GEO' && <p className="mt-2 text-ink-3">Reference GEO slot only. Public orbital elements are required for calculated contacts.</p>}
          {mission.orbit.tleSource && <p className="mt-1 text-[10px] text-ink-3">Orbital source: {mission.orbit.tleSource}</p>}
        </div>

        <div className="flex flex-wrap gap-1">
          <TextButton
            active={tracked === mission.id}
            aria-pressed={tracked === mission.id}
            onClick={() => setTracked(tracked === mission.id ? null : mission.id)}
            className="flex items-center gap-1"
            title="Follow (F)"
          >
            <Crosshair className="h-3.5 w-3.5" /> {tracked === mission.id ? 'Following' : 'Follow'}
          </TextButton>
          <TextButton
            onClick={() => getGlobeEngine()?.flyToMission(mission.id)}
            className="flex items-center gap-1"
          >
            <Navigation className="h-3.5 w-3.5" /> Fly to
          </TextButton>
          <TextButton
            onClick={() => getGlobeEngine()?.showGroundTrack(mission.id)}
            className="flex items-center gap-1"
          >
            <Footprints className="h-3.5 w-3.5" /> Ground track
          </TextButton>
          <TextButton
            className="flex items-center gap-1"
            onClick={async () => {
              try {
                const assetId = await registerMissionTwin(mission, dataset.network);
                useUI.getState().select({ assetId, subsystemId: null });
                navigate(`/twin/${encodeURIComponent(assetId)}`);
              } catch {
                toast('error', 'The spacecraft twin could not be prepared. Please retry.');
              }
            }}
          >
            <Box className="h-3.5 w-3.5" />
            Open twin
          </TextButton>
        </div>

        {mission.catalogue && (
          <section className="rounded-control border border-hairline p-3 text-xs text-ink-2" aria-label="Reviewed mission catalogue">
            <div className="font-semibold text-ink">{mission.catalogue.role} · {mission.catalogue.family}</div>
            <p className="mt-1">Payload: {mission.catalogue.instruments.join(', ')}.</p>
            <p className="mt-1">3D: {mission.catalogue.model}.</p>
            {mission.orbit.type === 'L1' && <p className="mt-1">Reference twin available. Current L1 ephemeris is not connected; no Earth-orbit position is shown.</p>}
            <p className="mt-1">{mission.catalogue.planningNote}</p>
            <details className="mt-2"><summary>Sources · reviewed {mission.catalogue.checkedAt}</summary>
              {mission.catalogue.sources.map((href,i)=><a key={href} className="mt-1 block underline" href={href} target="_blank" rel="noreferrer">Source {i+1}: {new URL(href).hostname}</a>)}
            </details>
          </section>
        )}
        <div className="divide-y divide-hairline rounded-control border border-hairline px-3">
          <Row label="Orbit">{orbitLabel(mission)}</Row>
          <Row label="Bands">
            <Bands bands={missionBands(mission)} />
          </Row>
          {mission.requirements.minutesPerYear !== undefined && (
            <Row label="Required">{fmtMinutes(mission.requirements.minutesPerYear / 365)} per day</Row>
          )}
          {mission.requirements.dataPerDayGB !== undefined && (
            <Row label="Data">{mission.requirements.dataPerDayGB} GB per day</Row>
          )}
          {mission.requirements.minPassDurationMin !== undefined && (
            <Row label="Minimum pass">{mission.requirements.minPassDurationMin} min</Row>
          )}
          {mission.requirements.slaMinimumPercent !== undefined && (
            <Row label="SLA floor">{mission.requirements.slaMinimumPercent}%</Row>
          )}
          {mission.noradId !== undefined && <Row label="NORAD">{String(mission.noradId)}</Row>}
        </div>

        {sla && (
          <div className="rounded-control border border-hairline px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-ink-3">SLA in window</span>
              <Chip tone={slaTone(sla.status)}>
                {pct(sla.percentage)} {sla.status}
              </Chip>
            </div>
            <div className="mt-1 text-ink-2">
              {fmtMinutes(sla.totalMinutes)} of {fmtMinutes(sla.requiredMinutes)} required, {sla.passes}{' '}
              passes
            </div>
            {sla.dataSLA && (
              <div className="text-ink-2">
                Data {sla.dataSLA.deliveredGB.toFixed(1)} of {sla.dataSLA.requiredGB.toFixed(1)} GB (
                {pct(sla.dataSLA.percentage)})
              </div>
            )}
            {Object.keys(sla.perAntenna).length > 0 && (
              <ul className="mt-2 flex flex-col gap-0.5">
                {Object.entries(sla.perAntenna)
                  .sort(([, a], [, b]) => b.minutes - a.minutes)
                  .map(([id, v]) => (
                    <li key={id} className="flex justify-between">
                      <button
                        type="button"
                        className="text-ink hover:text-accent"
                        onClick={() =>
                          useUI.getState().select({ antennaId: id, missionId: null, assetId: null })
                        }
                      >
                        {antennaName(id)}
                      </button>
                      <span className="font-mono text-ink-2">
                        {v.passes} passes, {fmtMinutes(v.minutes)}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}

        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Preferred antennas</div>
          <div className="flex flex-wrap gap-1">
            {mission.preferredAntennas.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => useUI.getState().select({ antennaId: id, missionId: null, assetId: null })}
                className="rounded-control border border-hairline px-1.5 py-0.5 text-ink-2 hover:text-ink"
              >
                {antennaName(id)}
              </button>
            ))}
            {mission.acceptableAlternatives.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => useUI.getState().select({ antennaId: id, missionId: null, assetId: null })}
                className="rounded-control border border-dashed border-hairline px-1.5 py-0.5 text-ink-3 hover:text-ink"
              >
                {antennaName(id)}
              </button>
            ))}
          </div>
        </div>

        {passes.length > 0 && (
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Next passes</div>
            <ul className="flex flex-col gap-0.5">
              {passes.map((p) => (
                <li
                  key={`${p.counterpart}-${p.startMs}`}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-control px-2 py-cell-y',
                    p.startMs <= nowMs ? 'bg-live/10 text-ink' : 'text-ink-2',
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    {antennaName(p.counterpart)}
                    {p.startMs <= nowMs && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-live">
                        now
                      </span>
                    )}
                  </span>
                  <span className="font-mono">
                    {fmtUtcShort(p.startMs, nowMs)} to {fmtUtcShort(p.endMs, p.startMs)}, {p.durationMin} min,{' '}
                    {Math.round(p.maxElevation)} deg
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (antenna) {
    const util = results?.antennaUtil[antenna.id];
    const schedule: Pass[] = results
      ? Object.entries(results.allocations)
          .flatMap(([missionId, byAntenna]) =>
            (byAntenna[antenna.id] ?? []).map((w) => ({ ...w, counterpart: missionId })),
          )
          .filter((w) => w.endMs >= nowMs)
          .sort((a, b) => a.startMs - b.startMs)
          .slice(0, 8)
      : [];
    const missionName = (id: string) => dataset.missions.find((m) => m.id === id)?.shortName ?? id;
    return (
      <div className="flex flex-col gap-3 p-panel text-data">
        <div>
          <div className="font-display text-base font-semibold text-ink">{antenna.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {antenna.network && <Chip tone="neutral">{antenna.network}</Chip>}
            <Chip tone={antenna.enabled === false ? 'offline' : 'nominal'}>
              {antenna.enabled === false ? 'Disabled' : 'Enabled'}
            </Chip>
            {antenna.capabilities.geoOnly && <Chip tone="neutral">GEO only</Chip>}
          </div>
          {antenna.description && <p className="mt-2 leading-snug text-ink-2">{antenna.description}</p>}
        </div>

        <div className="flex flex-wrap gap-1">
          <TextButton
            onClick={() => getGlobeEngine()?.flyToAntenna(antenna.id)}
            className="flex items-center gap-1"
          >
            <Navigation className="h-3.5 w-3.5" /> Fly to
          </TextButton>
          <TextButton
            onClick={() => {
              const assetId = registerAntennaTwin(antenna, dataset.network);
              useUI.getState().select({ assetId, subsystemId: null });
              navigate(`/twin/${encodeURIComponent(assetId)}`);
            }}
            className="flex items-center gap-1"
            title="Open the 3D twin of this antenna"
          >
            <Box className="h-3.5 w-3.5" /> Open twin
          </TextButton>
        </div>

        <div className="divide-y divide-hairline rounded-control border border-hairline px-3">
          <Row label="Location">
            {antenna.location.name ?? ''} {antenna.location.lat.toFixed(antenna.capabilities.monitoringOnly ? 6 : 3)}, {antenna.location.lon.toFixed(antenna.capabilities.monitoringOnly ? 6 : 3)}
          </Row>
          {antenna.capabilities.diameterM !== undefined && (
            <Row label="Aperture">
              {antenna.capabilities.diameterM} m {antenna.capabilities.type ?? ''}
            </Row>
          )}
          <Row label="Bands">
            {antenna.capabilities.monitoringOnly ? 'Installed configuration unverified' : <Bands bands={antenna.capabilities.bands} />}
          </Row>
          <Row label="Elevation">
            {antenna.capabilities.monitoringOnly ? 'Measured RF pattern unavailable' : `${antenna.coverage.elevationMinDeg ?? 5} to ${antenna.coverage.elevationMaxDeg ?? 90} deg`}
          </Row>
          {antenna.coverage.mask && <Row label="Horizon mask">{antenna.coverage.mask.length} points</Row>}
          {!antenna.capabilities.monitoringOnly && <Row label="Daily limit">{antenna.capabilities.maxHoursPerDay ?? 18} h</Row>}
          {antenna.capabilities.maintenanceWindowHrs ? (
            <Row label="Maintenance">{antenna.capabilities.maintenanceWindowHrs} h from 00:00Z</Row>
          ) : null}
        </div>

        {util && !antenna.capabilities.monitoringOnly && (
          <div className="rounded-control border border-hairline px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-ink-3">Utilisation in window</span>
              <Chip tone={utilTone(util.status)}>
                {pct(util.percentage)} {util.status}
              </Chip>
            </div>
            <div className="mt-1 text-ink-2">
              {fmtMinutes(util.scheduledMinutes)} of {fmtMinutes(util.availableMinutes)} available,{' '}
              {util.passes} passes
            </div>
          </div>
        )}

        <StationWeather antenna={antenna} />
        {schedule.length > 0 && (
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Schedule</div>
            <ul className="flex flex-col gap-0.5">
              {schedule.map((p) => (
                <li
                  key={`${p.counterpart}-${p.startMs}`}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-control px-2 py-cell-y',
                    p.startMs <= nowMs ? 'bg-live/10 text-ink' : 'text-ink-2',
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className="rounded-control hover:text-accent"
                      onClick={() =>
                        useUI.getState().select({ missionId: p.counterpart, antennaId: null, assetId: null })
                      }
                    >
                      {missionName(p.counterpart)}
                    </button>
                    {p.startMs <= nowMs && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-live">
                        now
                      </span>
                    )}
                  </span>
                  <span className="font-mono">
                    {fmtUtcShort(p.startMs, nowMs)} to {fmtUtcShort(p.endMs, p.startMs)}, {p.durationMin} min
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-4 text-xs text-ink-3">
      <div className="text-ink-2">Nothing selected.</div>
      <div>
        Click a satellite or antenna on the globe, or pick one from the strip. F follows the selected mission;
        Esc clears.
      </div>
    </div>
  );
}
